import { ReactElement } from 'react';

import { AppIcon, AppIcons, ICON_SIZES } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { SxProps, Tab, Tabs, Theme, Tooltip, Box, Button } from '@mui/material';

import { ColorToken } from '../designSystem/colors/colors';

const getIconColor = (isLast: boolean, disabled: boolean) => {
  if (disabled) {
    return 'Colors/Foreground/fg-disabled';
  }

  return isLast ? 'Colors/Base/White' : 'Colors/Foreground/fg-secondary';
};

const handleActionClick = async (
  action: ActionTabItem,
  disabled: boolean,
  onCopySuccessChange?: (show: boolean) => void,
) => {
  if (disabled || action.disabled) {
    return;
  }

  const result = action.onClick?.();

  // Support async onClick — only show tooltip after successful resolution
  if (result instanceof Promise) {
    try {
      await result;
    } catch (error) {
      console.error('[ActionTabs] Action failed:', error);

      return;
    }
  }

  if (action.tooltipOnClickText) {
    onCopySuccessChange?.(true);
    setTimeout(() => {
      onCopySuccessChange?.(false);
    }, 2000);
  }
};

const renderTooltip = (
  action: ActionTabItem,
  showCopySuccess: boolean,
  children: React.ReactElement,
) => {
  const withHoverTooltip = action.tooltipHoverText ? (
    <Tooltip title={action.tooltipHoverText} placement="top" arrow>
      {children}
    </Tooltip>
  ) : (
    children
  );

  if (!action.tooltipOnClickText) {
    return withHoverTooltip;
  }

  return (
    <Tooltip
      open={showCopySuccess}
      title={action.tooltipOnClickText}
      placement={action.tooltipPosition || 'right'}
      arrow
      disableFocusListener
      disableHoverListener
      disableTouchListener
      componentsProps={{
        popper: {
          sx: linkCopiedPopperSx,
        },
      }}
    >
      {withHoverTooltip}
    </Tooltip>
  );
};

export type ActionTabItem = Readonly<{
  id?: string;
  label?: string;
  onClick?: () => void | Promise<void>;
  icon?: AppIcons;
  tooltipOnClickText?: string;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  tooltipHoverText?: string;
  disabled?: boolean;
}>;

export type ActionTabsProps = Readonly<{
  actions: ActionTabItem[];
  showCopySuccess?: boolean;
  onCopySuccessChange?: (show: boolean) => void;
  borderColor?: ColorToken;
  disabled?: boolean;
  isLastButtonPrimary?: boolean;
}>;

export const ActionTabs = ({
  actions,
  showCopySuccess = false,
  onCopySuccessChange,
  borderColor,
  disabled = false,
  isLastButtonPrimary = false,
}: ActionTabsProps): ReactElement => {
  const labelled = actions.some((action) => action.label);

  if (labelled) {
    return (
      <Box sx={buttonContainerSx}>
        {actions.map((action, index) => {
          const isLast = actions.length - 1 === index;

          const button = (
            <Button
              id={action.id}
              key={index}
              onClick={
                disabled || action.disabled
                  ? undefined
                  : (e) => {
                      e.stopPropagation();
                      handleActionClick(action, disabled, onCopySuccessChange);
                    }
              }
              disabled={disabled || action.disabled}
              hierarchy={isLast && isLastButtonPrimary ? 'Primary' : 'Secondary gray'}
              size="md"
              startIcon={
                action.icon && (
                  <AppIcon
                    iconName={action.icon}
                    diameter="20px"
                    color={getIconColor(
                      isLast && isLastButtonPrimary,
                      disabled || action.disabled || false,
                    )}
                  />
                )
              }
            >
              {action.label}
            </Button>
          );

          return renderTooltip(action, showCopySuccess, button);
        })}
      </Box>
    );
  }

  return (
    <Tabs sx={tabsSx} value={labelled}>
      {actions.map((action, index) => {
        const tab = (
          <Tab
            id={action.id}
            key={index}
            sx={tabSx(borderColor, disabled || action.disabled || false, actions.length === 1)}
            disableRipple
            icon={
              action.icon && (
                <AppIcon
                  iconName={action.icon}
                  diameter="20px"
                  color={
                    disabled || action.disabled
                      ? 'Colors/Foreground/fg-disabled'
                      : 'Colors/Foreground/fg-secondary'
                  }
                />
              )
            }
            onClick={(e) => {
              e.stopPropagation();
              handleActionClick(action, disabled, onCopySuccessChange);
            }}
          />
        );

        return renderTooltip(action, showCopySuccess, tab);
      })}
    </Tabs>
  );
};
const tabsSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    padding: backrs.spacing['spacing-none'],
  },
  '& .MuiButtonBase-root': {
    padding: backrs.spacing['spacing-none'] + ' !important',
    minWidth: 'unset !important',
    maxHeight: ICON_SIZES['40px'],
    maxWidth: '50px',
  },
});

const tabSx =
  (borderColor?: ColorToken, disabled?: boolean, isSingleItem?: boolean): SxProps<Theme> =>
  ({ typography, backrs }) => ({
    border: `1px solid ${borderColor ? backrs.colors[borderColor] : backrs.colors['Colors/Base/Black']}`,
    borderRight: isSingleItem
      ? `1px solid ${borderColor ? backrs.colors[borderColor] : backrs.colors['Colors/Base/Black']}`
      : 'none',
    padding: backrs.spacing['spacing-none'],
    minHeight: ICON_SIZES['40px'],
    width: '64px',
    '&:first-of-type': {
      borderRadius: isSingleItem ? '8px' : '8px 0px 0px 8px',
    },
    '&:last-of-type': {
      borderRadius: isSingleItem ? '8px' : '0px 8px 8px 0px',
      borderRight: `1px solid ${borderColor ? backrs.colors[borderColor] : backrs.colors['Colors/Base/Black']}`,
    },
    ...typography['text-sm-semibold'],
    color: backrs.colors['Gray/700'],
    transition: 'all 0.3s ease-in-out',
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    '&.Mui-selected': {
      backgroundColor: `${backrs.colors['Colors/Background/bg-active']}`,
      color: backrs.colors['Gray/700'],
    },
    '&:hover': {
      backgroundColor: disabled
        ? backrs.colors['Colors/Background/bg-primary']
        : backrs.colors['Gray/200'],
      color: backrs.colors['Gray/700'],
    },
    cursor: disabled ? 'not-allowed' : 'pointer',
    textTransform: 'none',
  });

const linkCopiedPopperSx: SxProps<Theme> = {
  zIndex: 99999999999999,
};

const buttonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
});
